/*
 * @Description: 比赛状态管理Hook - 统一管理比赛状态、标题设置、倒计时和定时刷新
 * @Author: Claude
 * @Date: 2026-04-17
 */
import { ref, computed, onMounted, onUnmounted, watch, unref, isRef, isReadonly } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getMatchDetail } from '@/api/index.js'
import { parseTime, formatMatchTime } from '@/utils/index.js'
import { useHeader } from './useHeader.js'
import { showConfirm } from '@/utils/message.js'
import { MatchStatusMap, MatchStatus } from '@/views/contests/matchesType.js'

// 默认配置
const DEFAULT_OPTIONS = {
  autoFetch: true, // 是否自动获取数据
  autoSetHeader: true, // 是否自动设置Header
  autoSetSubtitle: true, // 是否自动设置副标题（仅在比赛未开始时）
  showConfirmOnEnd: true, // 比赛结束时是否显示确认弹窗
  liveTickInterval: 5000 * 2, // 赛中刷新间隔（毫秒）
  countdownOnEnd: true, // 是否启用倒计时结束回调
  checkIntervalWhenLive: 10 * 60 * 1000, // Live状态检查间隔（10分钟，毫秒）
}

/**
 * 格式化倒计时文本 - 使用 formatMatchTime
 * @param {number} seconds - 剩余秒数
 * @param {number} status - 比赛状态
 * @returns {string} 格式化后的倒计时文本
 */
function formatCountdown(seconds, status) {
  if (!seconds || seconds <= 0) {
    switch (status) {
      case MatchStatus.LIVE:
        return 'Live'
      case MatchStatus.COMPLETED:
        return 'Completed'
      default:
        return ''
    }
  }

  // 根据剩余秒数计算目标时间戳
  const targetTimestamp = Date.now() + seconds * 1000

  // 使用 formatMatchTime 格式化时间
  const { date } = formatMatchTime(targetTimestamp)

  switch (status) {
    case MatchStatus.NOT_STARTED:
      return date ? `Starts in ${date}` : ''
    case MatchStatus.LIVE:
      return date ? `Live · ${date}` : 'Live'
    case MatchStatus.COMPLETED:
      return 'Completed'
    default:
      return date || ''
  }
}

/**
 * 获取主标题文本
 * @param {Object} matchData - 比赛数据
 * @returns {string} 主标题
 */
function getMainTitle(matchData) {
  if (!matchData) return ''

  const teamA = matchData.teama?.shortName || matchData.teama?.name || 'Team A'
  const teamB = matchData.teamb?.shortName || matchData.teamb?.name || 'Team B'

  return `${teamA} vs ${teamB}`
}

/**
 * 获取副标题文本（仅在比赛未开始时返回倒计时）
 * @param {Object} matchData - 比赛数据
 * @returns {string} 副标题
 */
function getAutoSubtitle(matchData) {
  if (!matchData) return ''

  // 只有在比赛未开始状态时，才自动显示倒计时
  if (matchData.status === MatchStatus.NOT_STARTED && matchData.remainingTime > 0) {
    return formatCountdown(matchData.remainingTime, matchData.status)
  }

  // 如果有开始时间，使用 formatMatchTime 格式化显示
  if (matchData.timestampStart) {
    const { date, time } = formatMatchTime(matchData.timestampStart * 1000)
    if (date && time) {
      return `${date} · ${time}`
    }
    return time || date || ''
  }

  // 其他状态（Live/Completed）不自动设置副标题，由外部控制
  return ''
}

/**
 * 获取状态类型（用于CHeader样式）
 * @param {number} status - 比赛状态码
 * @returns {string} 状态类型
 */
function getStatusType(status) {
  switch (status) {
    case MatchStatus.LIVE:
      return 'live'
    case MatchStatus.COMPLETED:
      return 'completed'
    case MatchStatus.NOT_STARTED:
      return 'scheduled'
    default:
      return ''
  }
}

/**
 * useMatchStatus Hook - 比赛状态管理
 *
 * 副标题设置逻辑：
 * - 比赛未开始（Scheduled）：自动显示倒计时，每秒更新
 * - 比赛开始（Live）：停止自动设置副标题，可由外部通过 setSubtitle 方法设置
 * - 比赛结束（Completed）：停止自动设置副标题
 *
 * 特殊逻辑：
 * - 初次访问未开始：倒计时结束后弹窗提示"比赛已经开始"，点击进入 Contest 页面
 * - 初次访问 Live：启动10分钟定时检查，如果比赛结束弹窗提示回到首页
 * - 初次访问已结束/已取消：不做任何操作
 *
 * 支持多种方式传入赛事ID：
 * 1. 不传参数 - 自动从路由 params.mid 获取
 * 2. 传入静态值 - useMatchStatus(123)
 * 3. 传入ref - useMatchStatus(ref(123))
 * 4. 传入getter函数 - useMatchStatus(() => props.mid)
 * 5. 传入computed - useMatchStatus(computedMid)
 *
 * @param {number|string|Ref|Function|ComputedRef} mid - 比赛ID（可选，支持多种形式）
 * @param {Object} options - 配置选项
 * @returns {Object} 比赛状态和相关方法
 */
export function useMatchStatus(mid = null, options = {}) {
  const route = useRoute()
  const router = useRouter()
  const { setHeader, clearHeader, setStatusType } = useHeader()

  // 合并配置
  const opts = { ...DEFAULT_OPTIONS, ...options }

  // 获取比赛ID - 支持多种传入形式
  const matchId = computed(() => {
    // 如果传入了mid参数
    if (mid !== null) {
      // 如果是ref或computed，解包
      const unwrapped = unref(mid)
      // 如果是函数，执行获取返回值
      if (typeof unwrapped === 'function') {
        const result = unwrapped()
        return Number(result) || 0
      }
      return Number(unwrapped) || 0
    }
    // 默认从路由获取
    return Number(route.params.mid) || 0
  })

  // 记录最后一次使用的matchId，用于检测变化
  const lastMatchId = ref(0)

  // 响应式状态
  const matchInfo = ref({})
  const loading = ref(false)
  const error = ref(null)

  // 记录首次获取的状态（用于判断初次访问的状态）
  const initialStatus = ref(0)
  const hasInitialFetch = ref(false)

  // 计算属性
  const matchStatus = computed(() => matchInfo.value?.status || 0)
  const operationStatus = computed(() => matchInfo.value?.operationStatus || 0)
  const remainingTime = computed(() => matchInfo.value?.remainingTime || 0)

  const isLive = computed(() => matchStatus.value === MatchStatus.LIVE)
  const isCompleted = computed(() => matchStatus.value === MatchStatus.COMPLETED)
  const isScheduled = computed(() => matchStatus.value === MatchStatus.NOT_STARTED)
  const isAborted = computed(() => matchStatus.value === MatchStatus.ABORTED)

  // 倒计时相关
  const countdownText = ref('')
  const currentRemainingTime = ref(0)
  // 外部设置的副标题（优先使用）
  const customSubtitle = ref('')

  // 回调函数存储
  const countdownEndCallbacks = []
  const liveTickCallbacks = []

  // 定时器引用
  let countdownTimer = null
  let liveTickTimer = null
  let statusCheckTimer = null // Live状态10分钟检查定时器

  // 状态类型（用于CHeader样式）
  const statusType = computed(() => getStatusType(matchStatus.value))

  /**
   * 获取当前应显示的副标题
   * 优先级：外部设置的 > 自动计算的（仅未开始状态）
   */
  const currentSubtitle = computed(() => {
    // 如果有外部设置的副标题，优先使用
    if (customSubtitle.value) {
      return customSubtitle.value
    }
    // 否则在未开始状态时显示倒计时
    if (isScheduled.value) {
      return countdownText.value
    }
    return ''
  })

  /**
   * 显示比赛开始弹窗并跳转
   */
  const showMatchStartedDialog = () => {
    // 判断当前是否在 Contest 页面
    const currentRouteName = route.name
    const currentMid = route.params.mid
    const isReloadPage = currentRouteName === 'ContestDetail' && String(currentMid) === String(matchId.value)
    showConfirm({
      message: 'This match is no longer accepting entries',
      showCancelButton: false,
      confirmText: isReloadPage ? 'VIEW UPCOMING MATCHES' : 'Enter',
      title: 'Notice',
    }).then(confirmed => {
      if (confirmed) {
        if (isReloadPage) {
          // 已经在 Contest 页面，刷新当前页面
          // window.location.reload()
          // 已经在 Contest 页面，回到首页
          router.push({ name: 'Home' })
        } else {
          // 跳转到 Contest 页面
          router.push({
            name: 'ContestDetail',
            params: { mid: matchId.value },
          })
        }
      }
    })
  }

  /**
   * 显示比赛结束弹窗并返回首页
   */
  const showMatchEndedDialog = () => {
    showConfirm({
      message: 'This match has ended. Please return to the home page.',
      showCancelButton: false,
      confirmText: 'Return to Home',
      title: 'Notice',
    }).then(confirmed => {
      if (confirmed) {
        router.push({ name: 'Home' })
      }
    })
  }

  /**
   * 启动Live状态10分钟检查定时器
   */
  const startLiveStatusCheck = () => {
    // 清除现有定时器
    if (statusCheckTimer) {
      clearInterval(statusCheckTimer)
      statusCheckTimer = null
    }

    // 只在Live状态下启动10分钟检查
    if (isLive.value) {
      statusCheckTimer = setInterval(async () => {
        // 获取最新比赛状态
        await fetchMatchDetail(false)

        // 如果比赛已结束，显示弹窗
        if (isCompleted.value) {
          stopLiveStatusCheck()
          showMatchEndedDialog()
        }
      }, opts.checkIntervalWhenLive)
    }
  }

  /**
   * 停止Live状态检查定时器
   */
  const stopLiveStatusCheck = () => {
    if (statusCheckTimer) {
      clearInterval(statusCheckTimer)
      statusCheckTimer = null
    }
  }

  /**
   * 更新倒计时显示
   * 仅在比赛未开始时自动更新Header副标题
   */
  const updateCountdown = () => {
    if (currentRemainingTime.value > 0) {
      countdownText.value = formatCountdown(currentRemainingTime.value, matchStatus.value)
      currentRemainingTime.value--
    } else {
      // 倒计时结束
      clearInterval(countdownTimer)
      countdownTimer = null
      countdownText.value = formatCountdown(0, matchStatus.value)

      // 触发倒计时结束回调
      if (opts.countdownOnEnd) {
        countdownEndCallbacks.forEach(cb => {
          try {
            cb(matchInfo.value)
          } catch (e) {
            console.error('Countdown end callback error:', e)
          }
        })
      }

      // 如果是Scheduled状态倒计时结束，显示比赛开始弹窗并跳转
      if (isScheduled.value && hasInitialFetch.value && initialStatus.value === MatchStatus.NOT_STARTED) {
        showMatchStartedDialog()
      }
    }

    // 仅在比赛未开始状态且允许自动设置时，更新Header副标题
    if (opts.autoSetHeader && isScheduled.value && opts.autoSetSubtitle) {
      const mainTitle = getMainTitle(matchInfo.value)
      setHeader(mainTitle, countdownText.value)
    }
  }

  /**
   * 设置副标题（供外部调用）
   * @param {string} subtitle - 副标题文本
   */
  const setSubtitle = subtitle => {
    customSubtitle.value = subtitle

    // 如果允许自动设置Header，更新副标题
    if (opts.autoSetHeader) {
      const mainTitle = getMainTitle(matchInfo.value)
      setHeader(mainTitle, subtitle)
    }
  }

  /**
   * 清除自定义副标题（恢复自动计算）
   */
  const clearCustomSubtitle = () => {
    customSubtitle.value = ''

    // 如果在未开始状态，恢复显示倒计时
    if (opts.autoSetHeader && isScheduled.value) {
      const mainTitle = getMainTitle(matchInfo.value)
      setHeader(mainTitle, countdownText.value)
    }
  }

  /**
   * 启动倒计时
   * 仅在比赛未开始时启动
   */
  const startCountdown = () => {
    // 清除现有定时器
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }

    // 只有在未开始状态才启动倒计时
    if (!isScheduled.value) {
      countdownText.value = ''
      return
    }

    // 初始化剩余时间
    currentRemainingTime.value = remainingTime.value

    if (currentRemainingTime.value > 0) {
      // 立即更新一次
      updateCountdown()
      // 启动每秒更新的定时器
      countdownTimer = setInterval(updateCountdown, 1000)
    } else {
      countdownText.value = formatCountdown(0, matchStatus.value)

      // 如果初始状态是未开始且倒计时为0，显示比赛开始弹窗
      if (hasInitialFetch.value && initialStatus.value === MatchStatus.NOT_STARTED) {
        showMatchStartedDialog()
      }
    }
  }

  /**
   * 停止倒计时
   */
  const stopCountdown = () => {
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }

  /**
   * 启动赛中定时刷新
   */
  const startLiveTick = () => {
    // 清除现有定时器
    if (liveTickTimer) {
      clearInterval(liveTickTimer)
      liveTickTimer = null
    }

    // 只在Live状态下启动
    if (isLive.value) {
      liveTickTimer = setInterval(() => {
        // 触发回调
        liveTickCallbacks.forEach(cb => {
          try {
            cb(matchInfo.value)
          } catch (e) {
            console.error('Live tick callback error:', e)
          }
        })

        // 同时刷新比赛数据
        fetchMatchDetail(false)
      }, opts.liveTickInterval)
    }
  }

  /**
   * 停止赛中定时刷新
   */
  const stopLiveTick = () => {
    if (liveTickTimer) {
      clearInterval(liveTickTimer)
      liveTickTimer = null
    }
  }

  /**
   * 停止所有定时器
   */
  const stopAllTimers = () => {
    stopCountdown()
    stopLiveTick()
    stopLiveStatusCheck()
  }

  /**
   * 获取比赛详情
   * @param {boolean} withLoading - 是否显示loading
   */
  const fetchMatchDetail = async (withLoading = true) => {
    if (!matchId.value) {
      error.value = '比赛ID不能为空'
      return
    }

    if (withLoading) loading.value = true
    error.value = null

    try {
      const res = await getMatchDetail({ mid: matchId.value })

      if (res && res.resultData) {
        const oldStatus = matchStatus.value
        matchInfo.value = res.resultData
        lastMatchId.value = matchId.value

        // 记录首次获取的状态（只在第一次获取时记录）
        if (!hasInitialFetch.value) {
          initialStatus.value = res.resultData.status
          hasInitialFetch.value = true

          // 根据首次获取的状态进行不同的处理
          const initialStatusValue = res.resultData.status

          // 首次获取就是已结束或已取消：什么都不做
          if (initialStatusValue === MatchStatus.COMPLETED || initialStatusValue === MatchStatus.ABORTED) {
            // 不做任何操作
            console.log('Initial status is completed or aborted, no action needed')
          }
          // 首次获取是未开始：启动倒计时
          else if (initialStatusValue === MatchStatus.NOT_STARTED) {
            // 自动设置Header
            if (opts.autoSetHeader) {
              const mainTitle = getMainTitle(res.resultData)
              const subtitle = getAutoSubtitle(res.resultData)
              setHeader(mainTitle, subtitle)
              setStatusType(statusType.value)
            }

            // 启动倒计时
            startCountdown()
          }
          // 首次获取是Live：启动10分钟检查定时器
          else if (initialStatusValue === MatchStatus.LIVE) {
            // 自动设置Header
            if (opts.autoSetHeader) {
              const mainTitle = getMainTitle(res.resultData)
              setHeader(mainTitle, '')
              setStatusType(statusType.value)
            }

            // 启动赛中定时刷新
            startLiveTick()

            // 启动10分钟状态检查定时器
            startLiveStatusCheck()
          }
        } else {
          // 非首次获取，按正常逻辑处理

          // 自动设置Header
          if (opts.autoSetHeader) {
            const mainTitle = getMainTitle(res.resultData)

            // 根据比赛状态决定是否自动设置副标题
            let subtitle = ''
            if (isScheduled.value && opts.autoSetSubtitle) {
              // 未开始状态：显示倒计时或开始时间
              subtitle = getAutoSubtitle(res.resultData)
            } else if (customSubtitle.value) {
              // 其他状态：使用外部设置的副标题
              subtitle = customSubtitle.value
            }

            setHeader(mainTitle, subtitle)

            // 设置状态类型，用于CHeader显示状态标签
            setStatusType(statusType.value)
          }

          // 根据状态启动相应的定时器
          if (isScheduled.value) {
            // 未开始状态：启动倒计时
            startCountdown()
          } else if (isLive.value) {
            // 比赛中状态：停止倒计时，启动赛中刷新
            stopCountdown()
            startLiveTick()
          } else {
            // 其他状态：停止所有定时器
            stopAllTimers()
          }

          // 检查是否已结束且需要显示确认弹窗（从Live变为Completed的情况）
          if (isCompleted.value && opts.showConfirmOnEnd && oldStatus === MatchStatus.LIVE) {
            stopLiveStatusCheck()
            showMatchEndedDialog()
          }
        }
      } else {
        error.value = res.message || 'Failed to fetch match detail'
      }
    } catch (err) {
      error.value = err.message || 'Failed to fetch match detail'
    } finally {
      if (withLoading) loading.value = false
    }
  }

  /**
   * 手动刷新比赛数据
   */
  const refreshMatch = () => {
    return fetchMatchDetail(true)
  }

  /**
   * 切换赛事ID（用于动态切换比赛）
   * @param {number|string} newMid - 新的比赛ID
   */
  const switchMatch = newMid => {
    const targetId = Number(newMid)
    if (targetId && targetId !== matchId.value) {
      // 清理当前状态
      stopAllTimers()
      matchInfo.value = {}
      countdownText.value = ''
      currentRemainingTime.value = 0
      customSubtitle.value = ''
      hasInitialFetch.value = false
      initialStatus.value = 0

      // 如果是ref类型的mid，尝试更新它
      if (isRef(mid) && !isReadonly(mid)) {
        mid.value = targetId
      } else {
        // 否则直接获取新数据
        fetchMatchDetail()
      }
    }
  }

  /**
   * 注册倒计时结束回调
   * @param {Function} callback - 回调函数，接收matchInfo参数
   * @returns {Function} 取消订阅函数
   */
  const onCountdownEnd = callback => {
    if (typeof callback === 'function') {
      countdownEndCallbacks.push(callback)
    }
    // 返回取消订阅函数
    return () => {
      const index = countdownEndCallbacks.indexOf(callback)
      if (index > -1) {
        countdownEndCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * 注册赛中定时回调（每5秒）
   * @param {Function} callback - 回调函数，接收matchInfo参数
   * @returns {Function} 取消订阅函数
   */
  const onLiveTick = callback => {
    if (typeof callback === 'function') {
      liveTickCallbacks.push(callback)
    }
    // 返回取消订阅函数
    return () => {
      const index = liveTickCallbacks.indexOf(callback)
      if (index > -1) {
        liveTickCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * 注册状态变化回调
   * @param {Function} callback - 回调函数，接收(newStatus, oldStatus, matchInfo)参数
   * @returns {Function} 取消订阅函数
   */
  const onStatusChange = callback => {
    if (typeof callback === 'function') {
      const unwatch = watch(matchStatus, (newStatus, oldStatus) => {
        if (newStatus !== oldStatus) {
          callback(newStatus, oldStatus, matchInfo.value)
        }
      })
      return unwatch
    }
    return () => { }
  }

  /**
   * 清理资源
   */
  const cleanup = () => {
    stopAllTimers()
    countdownEndCallbacks.length = 0
    liveTickCallbacks.length = 0

    if (opts.autoSetHeader) {
      clearHeader()
    }
  }

  // 监听比赛ID变化，自动获取数据
  watch(
    matchId,
    (newVal, oldVal) => {
      if (newVal && newVal !== oldVal && opts.autoFetch) {
        // 清理旧数据
        stopAllTimers()
        matchInfo.value = {}
        customSubtitle.value = ''
        hasInitialFetch.value = false
        initialStatus.value = 0
        fetchMatchDetail()
      }
    },
    { immediate: true }
  )

  // 监听比赛状态变化
  watch(matchStatus, (newStatus, oldStatus) => {
    if (newStatus !== oldStatus) {
      // 状态变化时更新Header状态类型
      if (opts.autoSetHeader) {
        setStatusType(statusType.value)
      }

      // 如果变为Live状态
      if (newStatus === MatchStatus.LIVE) {
        // 停止倒计时
        stopCountdown()
        // 启动赛中定时器
        startLiveTick()
        // 如果是首次获取后状态变化，启动10分钟检查
        if (hasInitialFetch.value) {
          startLiveStatusCheck()
        }
      }

      // 如果从Live变为其他状态
      if (oldStatus === MatchStatus.LIVE && newStatus !== MatchStatus.LIVE) {
        stopLiveTick()
        stopLiveStatusCheck()

        // 如果变为结束状态，显示弹窗
        if (newStatus === MatchStatus.COMPLETED && hasInitialFetch.value) {
          showMatchEndedDialog()
        }
      }

      // 如果变为Scheduled状态（如从其他状态重置）
      if (newStatus === MatchStatus.NOT_STARTED) {
        startCountdown()
      }
    }
  })

  // 组件挂载时获取数据
  onMounted(() => {
    if (opts.autoFetch && matchId.value && !matchInfo.value.mid) {
      fetchMatchDetail()
    }
  })

  // 组件卸载时清理
  onUnmounted(() => {
    cleanup()
  })

  return {
    // 比赛信息
    matchInfo,
    matchStatus,
    operationStatus,
    matchId,

    // 状态判断
    isLive,
    isCompleted,
    isScheduled,
    isAborted,

    // 首次获取状态（供外部参考）
    initialStatus,
    hasInitialFetch,

    // 倒计时
    countdownText,
    remainingTime: currentRemainingTime,
    subtitle: currentSubtitle,

    // 加载和错误状态
    loading,
    error,

    // 状态类型（用于CHeader样式）
    statusType,

    // 方法
    fetchMatchDetail,
    refreshMatch,
    switchMatch,
    setSubtitle, // 设置副标题
    clearCustomSubtitle, // 清除自定义副标题
    onCountdownEnd,
    onLiveTick,
    onStatusChange,
    cleanup,
    stopCountdown,
    stopLiveTick,
    stopAllTimers,
    startCountdown,
    startLiveTick,

    // 工具函数（方便外部使用）
    formatCountdown,
    getMainTitle,
    getAutoSubtitle,
    getStatusType,
  }
}

export default useMatchStatus
